import React, { useMemo } from "react"

import { format } from "date-fns"

import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { formatCost } from "@/lib/routeCostCalculation"
import { WeekData, revenueByRoute } from "@/lib/weeklyPlan"

interface RevenueByRouteSheetProps {
  open: boolean
  onClose: () => void
  data: WeekData
  /** The day to break down, or null for the whole week. */
  dateStr: string | null
  dayLabel: string
  weekDays: Date[]
}

const RevenueByRouteSheet: React.FC<RevenueByRouteSheetProps> = ({
  open,
  onClose,
  data,
  dateStr,
  dayLabel,
  weekDays,
}) => {
  const routesWithRevenue = useMemo(
    () =>
      revenueByRoute(
        data.categories,
        dateStr !== null ? [dateStr] : weekDays.map((d) => format(d, "yyyy-MM-dd")),
      ),
    [data.categories, dateStr, weekDays],
  )

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
    >
      <SheetContent className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Haulage revenue — {dayLabel}</SheetTitle>
        </SheetHeader>
        <div className="mt-4">
          {routesWithRevenue.length === 0 ? (
            <p className="text-sm text-palette-jungleGreen500">
              No haulage revenue for this period.
            </p>
          ) : (
            <div className="space-y-2">
              {routesWithRevenue.map(({ route, revenue }) => (
                <div
                  key={route.routeId}
                  className="flex items-center justify-between py-2 px-3 rounded-lg bg-green-50 border border-green-100"
                >
                  <span className="text-sm font-medium text-palette-jungleGreen900">
                    {route.code}
                  </span>
                  <span className="text-sm font-semibold text-green-700 tabular-nums">
                    {formatCost(revenue)}
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 mt-2 border-t border-palette-jungleGreen200">
                <span className="text-sm font-semibold text-palette-jungleGreen900">Total</span>
                <span className="text-sm font-bold text-green-700 tabular-nums">
                  {formatCost(routesWithRevenue.reduce((s, x) => s + x.revenue, 0))}
                </span>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default RevenueByRouteSheet
